import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	ITicketInvoiceError,
	useSendTicketInvoices,
} from './useSendTicketInvoices';

/** Maps an invoice-send failure status to its user-facing message key. */
const getInvoiceErrorKey = (
	status: number,
	retryAfterSeconds?: number
): string => {
	switch (status) {
		case 429:
			return retryAfterSeconds
				? 'my_ticket_detail.invoice_error.429_seconds'
				: 'my_ticket_detail.invoice_error.429';
		case 409:
			return 'my_ticket_detail.invoice_error.409';
		case 503:
			return 'my_ticket_detail.invoice_error.503';
		case 502:
			return 'my_ticket_detail.invoice_error.502';
		default:
			return 'my_ticket_detail.invoice_error.generic';
	}
};

/**
 * Owns the "Rechnung per Email anfordern" action for the ticket detail:
 * sends the invoice e-mail(s) for `purchaseId` and holds the error message
 * shown in the bottom sheet on failure. The row itself renders the
 * loading/success/error icon; this hook feeds it `sendInvoice`/`handleError`
 * and exposes the sheet state.
 */
export const useTicketInvoice = (purchaseId: string | undefined) => {
	const { t } = useTranslation('ticketshop');
	const { sendInvoices } = useSendTicketInvoices();

	// Error shown in the bottom sheet; null hides it.
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const sendInvoice = useCallback(
		() => sendInvoices(purchaseId ?? ''),
		[sendInvoices, purchaseId]
	);

	const handleError = useCallback(
		(error: unknown) => {
			const { status, retryAfterSeconds } = (error as
				| ITicketInvoiceError
				| undefined) ?? { status: 0 };

			setErrorMessage(
				t(getInvoiceErrorKey(status, retryAfterSeconds), {
					seconds: retryAfterSeconds,
				})
			);
		},
		[t]
	);

	const clearError = useCallback(() => setErrorMessage(null), []);

	return { sendInvoice, handleError, errorMessage, clearError };
};
